import { useMemo } from 'react';
import { PAGE_TYPE } from '../types/pages.type';
import { getPercentageByTotal } from '../utilis/common';
import useAICalculationContext from './useAICalculationContext';
import useAICardDataContext from './useAICardDataContext';
import useAIFacilityContext from './useAIFacilityContext';
import useAIChartAndPatientContext from './useAIChartAndPatientContext';
import { generateChartAnalysis } from '../utilis/chart-analytics';

/**
 * Custom hook for Deceased Page AI Chat Context
 * Dedicated hook for Deceased page with deceased-specific terminology and logic
 */
export const useAIDeceasedChatContext = ({
  // Core data dependencies
  dbData,
  filterListData,
  patientList,
  activeFacilities,
  facilities,
  dynamicCards,
  
  // Filter and state dependencies
  filter,
  transferType,
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  projectionDays,
  lastADTDate,
  
  // UI state dependencies
  deceasedCards,
  rangesSet,
  isLayoutMode,
  
  // Function dependencies
  getPriorityNumber,
  lockedTotal,
  lockedByFacility,
  isCensusTotalLocked,
  facilityPercentage,
  
  // Metadata dependencies
  DECEASED_CUSTOM_CARD_DATA,
  tooltipsInfo,
  
  // Service dependencies
  aiCensusActionsService,
  aiReportActionsService,
  aiTransferActionsService,
  aiDialogActionsService,
  aiDateSuggestionsService,
  
  // Chart data dependencies
  chartData = null,
  chartAggregationType = 'unknown',
  currentChartFilter = null
}) => {
  // Use sub-hooks for different aspects of the context
  const calculationContext = useAICalculationContext({
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst
  });

  const cardDataContext = useAICardDataContext({
    pageType: PAGE_TYPE.DECEASED,
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards,
    patientList,
    HOSPITAL_CUSTOM_CARD_DATA: DECEASED_CUSTOM_CARD_DATA,
    COMMUNITY_CUSTOM_CARD_DATA: null
  });

  const facilityContext = useAIFacilityContext({
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType: PAGE_TYPE.DECEASED
  });

  const chartAndPatientContext = useAIChartAndPatientContext();

  return useMemo(() => {
    // Calculate enhanced metrics for AI analysis
    const totalDeceased = dbData?.total || 0;
    
    // Get active filters count
    const activeFiltersCount = cardFilter ? Object.values(cardFilter).filter(arr => Array.isArray(arr) && arr.length > 0).length : 0;
    
    // Calculate data availability
    const dataCategories = filterListData ? Object.keys(filterListData).filter(key => 
      Array.isArray(filterListData[key]) && filterListData[key].length > 0
    ) : [];

    // Get the correct percentage base (same logic as cards use)
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData);

    // IMPORTANT: Deceased Page Terminology definitions for AI
    const terminology = {
      deceased: {
        label: "Deceased",
        meaning: "Residents who passed away during the selected time frame",
        description: "These are residents who died while in the facility. The Deceased dashboard tracks all deceased residents and their associated data.",
        keyTerms: ["deceased", "passed away", "death", "mortality", "died"],
        dataField: "total"
      },
      important: "Deceased page tracks residents who passed away. All analytics focus on understanding patterns, trends, and factors related to resident mortality."
    };

    // Percentage Calculation Context (critical for accurate AI responses)
    const percentageContext = {
      currentMethod: percentageBy,
      currentPercentageBase,
      percentageAgainst,
      filterTotal,
      specialComparisonSet,
      isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus,
      lockedTotalBy,
      transferType,
      calculation: {
        description: `Percentages calculated using ${percentageBy} method`,
        baseValue: currentPercentageBase,
        explanation: specialComparisonSet 
          ? `Using filterTotal (${filterTotal}) as base because specialComparisonSet is true`
          : `Using percentageAgainst (${percentageAgainst}) as base from ${percentageBy} method`
      },
      priorityFiltering: {
        activePriorityFilters: cardFilter?.priorityData || [],
        totalActivePriorityFilters: cardFilter?.priorityData?.length || 0,
        firstPriorityCard: cardFilter?.priorityData?.[0]?.type || null,
        explanation: `When priority filters are applied, percentage bases change:
          - First priority filter uses mainNumPercentage or totalForPercentage
          - Subsequent filters use filtered resident count as denominator
          - Example: If "Insurance" filter is applied first, percentages for other cards use Insurance count as base
          - This is why filtering affects percentage calculations across all cards`
      },
      scalingLogic: `Percentages are scaled when:
        - isComparingAgainstAvgCensus is true
        - lockedTotalBy === "census"
        - First priority item AND no transfer type filter applied`
    };

    // Census Percentage Settings for AI interaction
    const censusSettings = (() => {
      try {
        const settings = aiCensusActionsService?.getCurrentPercentageSettings();
        return {
          currentMethod: settings?.currentMethod,
          customLabel: settings?.customLabel,
          customPercentages: settings?.customPercentages,
          availableMethods: settings?.availableMethods,
          censusData: settings?.censusData,
          explanations: {
            bedCapacity: aiCensusActionsService?.getPercentageExplanation('Bed Capacity'),
            censusAsOfNow: aiCensusActionsService?.getPercentageExplanation('census as of now'),
            censusAverage: aiCensusActionsService?.getPercentageExplanation('Average census of time frame selected')
          },
          actionSuggestions: aiCensusActionsService?.getAIActionSuggestions()
        };
      } catch (error) {
        return {
          currentMethod: percentageBy,
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Current state
    const currentState = {
      selectedTransferType: transferType,
      activeFiltersCount,
      availableDataCategories: dataCategories,
      selectedCards: deceasedCards?.length || 0,
      totalCards: deceasedCards?.length || 0,
      hasComparison: !!rangesSet,
      isLayoutMode,
      facilityInfo: {
        hasMultipleFacilities: activeFacilities?.length > 1,
        facilityCount: activeFacilities?.length || 0,
        activeFacilities: activeFacilities
      }
    };

    // Filter information
    const filters = {
      dateRange: {
        startDate: filter?.startDate,
        endDate: filter?.endDate,
        isCustomRange: filter?.startDate && filter?.endDate
      },
      facilities: {
        active: activeFacilities,
        count: activeFacilities?.length || 0
      },
      cardFilters: cardFilter,
      priorityFilters: cardFilter?.priorityData || []
    };

    // Data structure for AI analysis
    const dataStructure = {
      dbData: {
        available: !!dbData,
        recordCount: totalDeceased,
        hasDeceasedData: totalDeceased > 0
      },
      filterListData: {
        available: !!filterListData,
        categories: dataCategories,
        totalCategories: dataCategories.length
      },
      patientList: {
        available: !!patientList?.list,
        count: patientList?.list?.length || 0,
        hasNinetyDaysData: !!(patientList?.ninetyDaysData?.length),
        hasDeceasedData: !!(patientList?.list?.length),
        hasCustomTabs: !!(patientList?.customTabs?.length)
      }
    };

    // Available actions for AI suggestions
    const availableActions = {
      // Transfer Type Actions (Limited for Deceased)
      canChangeTransferType: false, // Deceased doesn't have transfer types
      
      // Filter Actions
      canApplyFilters: dataCategories.length > 0,
      canApplyCardFilters: true,
      canApplyMultipleFilters: true,
      canToggleSpecificItems: true,
      canResetFilters: activeFiltersCount > 0,
      canToggleAllFilters: true,
      canClearSpecificFilters: true,
      
      // Report Generation Actions
      canGenerateReports: totalDeceased > 0,
      canGeneratePdfReports: totalDeceased > 0,
      canGenerateExcelReports: totalDeceased > 0,
      canSetupAutomatedReports: totalDeceased > 0,
      canGenerateCardSpecificReports: dataCategories.length > 0,
      canGenerateFilteredReports: activeFiltersCount > 0,
      
      // Date and Time Actions
      canChangeDateRange: true,
      canSuggestDateRanges: true,
      canApplyDateFilters: true,
      
      // Percentage and Census Actions
      canChangePercentageMethod: true,
      canAddCustomPercentage: true,
      canExplainPercentageCalculations: true,
      canChangeCensusMethod: true,
      canLockTotals: true,
      canUnlockTotals: true,
      
      // Layout and Display Actions
      canToggleCards: deceasedCards?.length > 0,
      canChangeLayout: true,
      canCompareData: true,
      canAnalyzeData: totalDeceased > 0,
      
      // Chart and Details Actions
      canViewChartData: totalDeceased > 0,
      canAnalyzeChartTrends: totalDeceased > 0,
      canViewResidentDetails: totalDeceased > 0,
      canAnalyzeDeceasedPatterns: totalDeceased > 0,
      canCompareTimePeriods: totalDeceased > 0,
      canViewDailyTrends: totalDeceased > 0,
      canViewWeeklyTrends: totalDeceased > 0,
      canViewMonthlyTrends: totalDeceased > 0,
      canViewYearlyTrends: totalDeceased > 0,
      
      // Advanced Actions
      canExplainDataRelationships: true,
      canProvideInsights: totalDeceased > 0,
      canSuggestOptimalFilters: dataCategories.length > 0,
      canExplainFilterImpact: activeFiltersCount > 0,
      canAnalyzeMortalityTrends: totalDeceased > 0
    };

    // Report Actions Context
    const reportActions = (() => {
      try {
        const reportStatus = aiReportActionsService?.getCurrentReportStatus();
        const reportOptions = aiReportActionsService?.getAvailableReportOptions();
        return {
          currentStatus: reportStatus,
          availableCards: reportStatus?.availableCards,
          canGenerateReports: reportStatus?.canGenerateReports,
          reportTypes: reportStatus?.reportTypes,
          automatedTypes: reportStatus?.automatedTypes,
          suggestions: aiReportActionsService?.getAIActionSuggestions(),
          options: reportOptions
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Transfer Actions Context (Limited for Deceased)
    const transferActions = (() => {
      try {
        const transferStatus = aiTransferActionsService?.getCurrentTransferStatus();
        return {
          currentStatus: transferStatus,
          availableTypes: [], // No transfer types for deceased
          currentType: null,
          currentLabel: 'All Deceased',
          currentCount: totalDeceased,
          totalAll: totalDeceased,
          suggestions: [],
          note: 'Deceased page does not have transfer type selection'
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Dialog Actions Context
    const dialogActions = (() => {
      try {
        const dialogStatus = aiDialogActionsService?.getCurrentDialogStatus();
        return {
          available: dialogStatus?.initialized,
          canOpenChartDialogs: dialogStatus?.canOpenChartDialogs,
          canOpenResidenceDialogs: dialogStatus?.canOpenResidenceDialogs,
          canOpenBuildingDialogs: dialogStatus?.canOpenBuildingDialogs,
          availableActions: dialogStatus?.availableActions,
          suggestions: [
            {
              type: 'chart_view',
              description: 'Open chart view for deceased trend analysis',
              triggers: ['chart', 'graph', 'trend', 'over time']
            },
            {
              type: 'residence_list',
              description: 'Show resident list for detailed deceased breakdown',
              triggers: ['residents', 'list', 'who are', 'breakdown']
            },
            {
              type: 'building_view',
              description: 'Show facility breakdown for deceased residents',
              triggers: ['facilities', 'buildings']
            }
          ]
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Locked Functionality Context
    const lockedFunctionality = {
      currentState: {
        lockedTotal,
        lockedByFacility,
        lockedTotalBy,
        isCensusTotalLocked,
        facilityPercentage
      },
      isLocked: !!lockedTotal || !!lockedByFacility?.length,
      lockType: lockedTotalBy,
      lockDescription: lockedTotalBy === 'census' 
        ? 'Percentages locked to census data'
        : lockedTotalBy 
          ? `Percentages locked to ${lockedTotalBy}`
          : 'No percentage lock applied',
      canLock: true,
      canUnlock: !!lockedTotal || !!lockedByFacility?.length,
      lockOptions: {
        byCensus: {
          type: 'census',
          description: 'Lock percentages to census data',
          available: true
        },
        byFacility: {
          type: 'facility',
          description: 'Lock percentages by facility',
          available: activeFacilities?.length > 1
        }
      }
    };

    // Date Filtering Context
    const dateFiltering = {
      currentRange: filter,
      hasDateConstraints: false,
      canSuggestDates: true,
      validationInfo: aiDateSuggestionsService?.getCurrentValidationInfo()
    };

    // Filter Logic Explanation (deceased-specific)
    const filterLogic = {
      description: 'Deceased page uses filter logic from deceased-common.js',
      keyFunctions: {
        updateFilterListData: 'Main filter processing function that applies all card filters',
        priorityFilter: 'Handles priority-based filtering where first filter affects percentage calculations',
        checkOnlyOneFilter: 'Determines if only one filter type is active',
        updateFacilityPercentageTotal: 'Calculates facility-wise percentage totals'
      },
      filterFlow: `1. Apply card filters (insurance, doctors, floors, days, 90-day analysis, etc.)
          2. Calculate priority filter impact on percentage base
          3. Update locked totals if applicable
          4. Process facility-wise percentages
          5. Update filter totals and percentage bases
          6. Recalculate all card percentages based on new base`,
      prioritySystem: {
        explanation: `Priority filters change percentage calculation base:
          - First applied filter becomes the denominator for other card percentages
          - Example: If insurance filter is applied first, other card percentages use that count as base`,
        currentPriority: cardFilter?.priorityData?.[0]?.type || null,
        priorityFilters: cardFilter?.priorityData || []
      }
    };

    // Card tooltip information for AI understanding
    const cardTooltips = {
      deceasedPage: tooltipsInfo?.deceasedPage || {},
      availableCards: Object.keys(tooltipsInfo?.deceasedPage || {}),
      cardDescriptions: Object.entries(tooltipsInfo?.deceasedPage || {}).map(([cardKey, description]) => ({
        cardKey,
        description,
        cardType: cardKey.toLowerCase().replace(/[^a-z0-9]/g, '')
      }))
    };

    // CRITICAL: Chart Data Analytics for AI
    const chartAnalytics = (() => {
      if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        return {
          available: false,
          message: 'No chart data available. Chart data will be available when viewing chart visualizations or analyzing deceased trends over time.',
          note: 'User can ask to view chart or trends to access this data'
        };
      }

      const analysis = generateChartAnalysis(chartData, chartAggregationType);
      
      return {
        available: true,
        aggregationType: chartAggregationType,
        currentFilter: currentChartFilter,
        analysis,
        
        // CRITICAL: Question-answering guides for AI (deceased-specific)
        questionAnsweringGuides: {
          'Which week had the most deaths?': {
            answer: `Use analysis.peak to find the period with highest value`,
            example: `The ${analysis.peak?.label} had the most deceased residents with ${analysis.peak?.value} deaths`
          },
          'Are deaths trending up or down?': {
            answer: `Use analysis.trend.direction and analysis.trend.description`,
            example: `Deceased residents show a ${analysis.trend?.direction} trend. ${analysis.trend?.description}`
          },
          'What happened after the peak mortality period?': {
            answer: `Use analysis.postPeakAnalysis for detailed post-peak behavior`,
            example: analysis.postPeakAnalysis?.available 
              ? `After the peak, ${analysis.postPeakAnalysis.immediateResponse.description}. ${analysis.postPeakAnalysis.analysis}`
              : 'Peak occurs at the end of the period'
          },
          'Show mortality pattern changes': {
            answer: `Use analysis.periodOverPeriodChanges for all period-to-period changes`,
            example: `Period-over-period changes available for ${analysis.periodOverPeriodChanges?.length} transitions`
          },
          'What is the average mortality rate?': {
            answer: `Use analysis.statistics.mean, median, and other stats`,
            example: `Average: ${analysis.statistics?.mean} deceased per ${chartAggregationType}, Median: ${analysis.statistics?.median}`
          },
          'Are there any mortality patterns?': {
            answer: `Use analysis.patterns to identify spikes, drops, and recurring patterns`,
            example: `${analysis.patterns?.description}. Spikes found: ${analysis.patterns?.spikes?.length}, Drops: ${analysis.patterns?.drops?.length}`
          }
        },
        
        // Quick reference data
        summary: {
          totalDataPoints: chartData.length,
          dateRange: `${analysis.dateRange?.start} to ${analysis.dateRange?.end}`,
          peakValue: analysis.peak?.value,
          peakPeriod: analysis.peak?.label,
          averageValue: analysis.statistics?.mean,
          medianValue: analysis.statistics?.median,
          trendDirection: analysis.trend?.direction,
          trendStrength: analysis.trend?.strength,
          overallChange: analysis.trend?.change,
          overallChangePercentage: analysis.trend?.changePercentage,
          largestIncrease: analysis.largestIncrease 
            ? `${analysis.largestIncrease.description}`
            : 'N/A',
          largestDecrease: analysis.largestDecrease 
            ? `${analysis.largestDecrease.description}`
            : 'N/A',
          volatility: analysis.summary?.volatility,
          dataQuality: analysis.summary?.dataQuality
        }
      };
    })();

    // Raw data for detailed analysis (limited for performance)
    const rawData = {
      dbData: dbData ? {
        total: dbData.total,
        censusAverage: dbData.censusAverage,
        // CRITICAL: Include ALL raw records for accurate cross-filtering (removing limit for accuracy)
        data: Array.isArray(patientList?.list) ? patientList.list : []
      } : null,
      sampleFilterData: filterListData ? (() => {
        // Start with first 5 entries (truncated)
        const sample = Object.fromEntries(
          Object.entries(filterListData).slice(0, 5).map(([key, value]) => [
            key, Array.isArray(value) ? value.slice(0, 10) : value
          ])
        );

        // CRITICAL: Always include ALOS custom tab data if it exists
        // Find and include any ALOS-related entries that might be outside the first 5
        Object.entries(filterListData).forEach(([key, value]) => {
          if (key.toLowerCase().includes('averagelengthofstay') ||
              key.toLowerCase().includes('avglos') ||
              key === 'averageStayDaysData') {
            sample[key] = Array.isArray(value) ? value : value;
          }
        });

        return sample;
      })() : null,
      // CRITICAL: Patient data for AI analysis
      patientData: (() => {
        if (!patientList?.list || !Array.isArray(patientList.list) || patientList.list.length === 0) {
          return null;
        }

        // Get diffDashboardPatients for ALOS calculation (contains admission patients with transferDay)
        const diffDashboardPatients = Array.isArray(patientList?.diffDashboardPatients)
          ? patientList.diffDashboardPatients
          : [];

        return {
          totalPatients: patientList.list.length,
          // CRITICAL: Pass ALL fields from patient list (backend already excludes logs, highlighter, etc.)
          // This includes: standard fields + dynamic question fields + all supplementary fields
          patients: patientList.list.map(patient => {
            const { __v, ...patientData } = patient; // Exclude only MongoDB version key
            return {
              ...patientData, // Spread ALL fields from patient (includes dynamic question fields)
              id: patient._id // Ensure id is always available
            };
          }),
          // Also include diffDashboardPatients for more accurate ALOS calculations
          diffDashboardPatients: diffDashboardPatients.map(p => ({
            _id: p._id,
            refPatientId: p.refPatientId,
            transferDay: p.transferDay,
            day: p.day,
            dateOfADT: p.dateOfADT
          }))
        };
      })()
    };
      
    // CRITICAL: Data Validation Rules for AI
    const dataValidationRules = {
      forExactCounts: {
        rule: "ALWAYS use item.actualValue or item.total for 'how many' questions",
        wrongFields: ["scaledValue", "projectedValue", "scaledPercentage"],
        correctFields: ["actualValue", "total", "value"],
        example: "For 'How many deceased?', use actualValue, NOT scaledValue"
      },
      forPercentages: {
        rule: "Use calculatedPercentage or scaledPercentage for percentage questions",
        correctFields: ["calculatedPercentage", "scaledPercentage", "percentage"]
      },
      forProjections: {
        rule: "Use projectedValue only for projection/forecast questions",
        correctFields: ["projectedValue", "projectedPercentage"]
      },
      commonMistakes: {
        mistake1: "Using scaledValue instead of actualValue for counts",
        mistake2: "Applying rounding to base counts",
        mistake3: "Using projected values for current data questions",
        mistake4: "Confusing mortality rate percentages with raw counts"
      }
    };

    // CRITICAL: Common User Questions and How AI Should Respond (deceased-specific)
    const commonQuestions = {
      "How many residents deceased?": {
        answer: "Use dbData.total for the exact count",
        calculation: "dbData.total gives the EXACT count",
        dataSource: "metrics.totalDeceased or rawData.dbData.total"
      },
      "Which doctor had the most deceased residents?": {
        answer: "Use cardData.doctors.topDoctors or sort cardData.doctors.itemsWithPercentages by actualValue",
        dataSource: "cardData.doctors.itemsWithPercentages"
      },
      "Which floor had the most deaths?": {
        answer: "Use cardData.floors.topFloors or sort cardData.floors.itemsWithPercentages by actualValue",
        dataSource: "cardData.floors.itemsWithPercentages"
      },
      "What day of the week has most deaths?": {
        answer: "Use cardData.days.itemsWithPercentages and find highest actualValue",
        dataSource: "cardData.days.dayBreakdown or cardData.days.itemsWithPercentages"
      },
      "Why did percentages change when I filtered?": {
        answer: "Priority filter system changes percentage denominators",
        explanation: "First filter becomes new base for other card percentages"
      },
      "How are projections calculated?": {
        answer: "Use projectionPerMonth() function with current values and date range",
        formula: "For periods ≤30 days: (projectionDays * total) / actualDays"
      },
      "Why are some percentages different?": {
        answer: "Different cards may use different percentage bases depending on priority filters",
        explanation: "Check percentageContext.priorityFiltering for active priority filters"
      },
      "What trends should I be concerned about?": {
        answer: "Look for patterns that deviate from expected healthcare norms",
        analysis: "Use contextual anomaly detection considering expected patterns, statistical distribution, and domain knowledge"
      },
      "Are there any unusual patterns in the data?": {
        answer: "Analyze each card for deviations from expected patterns",
        method: "Compare actual distribution against expected patterns using healthcare domain knowledge"
      },
      "What is the mortality rate?": {
        answer: "Use metrics.mortalityRate for the calculated rate",
        calculation: "mortalityRate = (totalDeceased / currentPercentageBase) * 100",
        dataSource: "metrics.mortalityRate"
      }
    };

    // Core metrics
    const metrics = {
      totalDeceased,
      totalTransfers: totalDeceased, // Alias for consistency with other pages
      mortalityRate: currentPercentageBase > 0 ? ((totalDeceased / currentPercentageBase) * 100).toFixed(1) : 0,
      filterTotal,
      percentageAgainst,
      projectionDays,
      lastADTDate,
      // Deceased page doesn't have transfer types like Hospital (planned/unplanned)
      // or Community (Safe Discharge/SNF/AMA), so we don't include those metrics
      hasTransferTypes: false,
      dateRange: `${filter?.startDate || 'N/A'} to ${filter?.endDate || 'N/A'}`
    };

    // Enhanced context with structured data
    return {
      pageType: PAGE_TYPE.DECEASED,
      pageData: {
        // CRITICAL: Calculation Functions
        ...calculationContext,
        
        // CRITICAL: Card Data - with explicit ALOS extraction for custom tabs
        cardData: (() => {
          const baseCardData = cardDataContext.cardData;

          // Check for ALOS custom tab in filterListData and add to cardData
          if (patientList?.customTabs) {
            const alosTab = patientList.customTabs.find(tab => {
              const title = (tab.title || tab.label || '').toLowerCase();
              return title.includes('average') && title.includes('length') && title.includes('stay');
            });

            if (alosTab && alosTab.accessor) {
              const alosData = filterListData?.[alosTab.accessor];
              if (Array.isArray(alosData) && alosData.length > 0) {
                // Extract ALOS value from custom tab data
                const avgItem = alosData[0];
                return {
                  ...baseCardData,
                  avgLos: {
                    total: avgItem.total || avgItem.value || 0,
                    originalTotal: avgItem.originalTotal ?? avgItem.total ?? avgItem.value ?? 0,
                    count: avgItem.count || alosData.length,
                    items: alosData,
                    unit: 'days',
                    source: 'custom_tab',
                    accessor: alosTab.accessor
                  }
                };
              }
            }
          }

          return baseCardData;
        })(),
        currentPercentageBase: cardDataContext.currentPercentageBase,
        calculateCardPercentages: cardDataContext.calculateCardPercentages,
        
        // CRITICAL: Facility Analysis
        ...facilityContext,
        
        // Active facilities
        activeFacilities,
        
        // CRITICAL: Chart and Patient Details
        ...chartAndPatientContext,

        // Core metrics
        metrics,

        // IMPORTANT: Terminology definitions for AI understanding
        terminology,

        // Percentage Calculation Context
        percentageContext,

        // Census Percentage Settings
        censusSettings,
        
        // Current state
        currentState,
        
        // Filter information
        filters,
        
        // Data structure for AI analysis
        dataStructure,
        
        // Available actions for AI suggestions
        availableActions,

        // Report Actions Context
        reportActions,

        // Transfer Actions Context
        transferActions,

        // Dialog Actions Context
        dialogActions,

        // Locked Functionality Context
        lockedFunctionality,

        // Date Filtering Context
        dateFiltering,

        // Filter Logic Explanation
        filterLogic,
        
        // Card tooltip information
        cardTooltips,
        
        // Raw data for detailed analysis
        rawData,

        // CRITICAL: Data Validation Rules for AI
        dataValidationRules,

        // CRITICAL: Common User Questions
        commonQuestions,

        // CRITICAL: Card metadata
        cardMetadata: DECEASED_CUSTOM_CARD_DATA,

        // CRITICAL: Custom/Dynamic Cards
        customCards: {
          dynamicCards: dynamicCards || [],
          customTabs: patientList?.customTabs || []
        },

        // CRITICAL: Chart Analytics
        chartAnalytics,

        // CRITICAL: Chart interpretation instructions for AI
        chartInterpretation: {
          description: 'How to answer chart-related questions for deceased residents',
          instructions: [
            '1. Check if chartAnalytics.available is true',
            '2. Use chartAnalytics.analysis for comprehensive data',
            '3. Reference chartAnalytics.questionAnsweringGuides for common questions',
            '4. Provide specific numbers, dates, and percentages from the analysis',
            '5. Always mention the aggregation type (daily, weekly, monthly)',
            '6. For trends, use analysis.trend for direction and strength',
            '7. For peaks, use analysis.peak with specific period and value',
            '8. For changes, use analysis.periodOverPeriodChanges for detailed breakdowns',
            '9. For patterns, use analysis.patterns for spikes, drops, and cycles',
            '10. For post-peak analysis, use analysis.postPeakAnalysis'
          ],
          commonMistakes: [
            'Not specifying which week/period when answering "highest" questions about mortality',
            'Forgetting to include percentage changes in comparisons',
            'Not mentioning the context (date range, aggregation type)',
            'Providing vague answers instead of specific numbers',
            'Not explaining what happened after significant events',
            'Using insensitive language when discussing deceased residents'
          ],
          bestPractices: [
            'Always include specific numbers and percentages',
            'Mention the time period (week of X, month of Y)',
            'Provide context about the overall mortality trend',
            'Compare to previous periods for context',
            'Explain the significance of changes in mortality patterns',
            'Use the exact period labels from the data (e.g., "Week of Aug 24-30")',
            'Be sensitive and professional when discussing deceased residents'
          ]
        }
      },
      facilityIds: activeFacilities,
      timestamp: new Date().toISOString()
    };
  }, [
    // Core data dependencies
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    dynamicCards,
    
    // Filter and state dependencies
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    
    // UI state dependencies
    deceasedCards,
    rangesSet,
    isLayoutMode,
    
    // Function dependencies
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    
    // Metadata dependencies
    DECEASED_CUSTOM_CARD_DATA,
    tooltipsInfo,
    
    // Service dependencies
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    
    // Sub-hook dependencies
    calculationContext,
    cardDataContext.calculateCardPercentages,
    cardDataContext.cardData,
    cardDataContext.currentPercentageBase,
    chartAndPatientContext,
    facilityContext,
    
    // Chart data dependencies
    chartData,
    chartAggregationType,
    currentChartFilter
  ]);
};

export default useAIDeceasedChatContext;

